import {
	InboxV2ListProps,
	InboxV2ListRenderProps,
	SelectionProps,
} from '@copilot/common/pages/inboxV2/components/listing/types';
import isEmpty from 'lodash/isEmpty';
import { MESSAGE_LIST_TEST_ID } from '@copilot/common/pages/inboxV2/constants';
import InboxPageV2NoMessagePlaceholder from '@copilot/common/pages/inboxV2/components/listing/inboxV2NoMessagePlaceholder';
import { Button, Checkbox, Flex, Popconfirm, Space, Spin } from 'antd';

import styles from './inboxV2List.module.less';
import InboxV2SearchBar from '@copilot/common/pages/inboxV2/components/inboxPageV2SearchBar';
import { useState } from 'react';
import pullAll from 'lodash/pullAll';
import isNil from 'lodash/isNil';
import HidableBottomPanel from '@copilot/common/components/containers/hidableBottomPanel';
import Text from 'antd/lib/typography';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import {
	ADD_TO_CAMPAIGN_BATCH,
	APPLY_TAGS_BATCH,
	BATCH_ACTION_TITLE,
	DELETE_BATCH,
	LOAD_MORE_BUTTON,
	LOAD_MORE_BUTTON_TEST_ID,
	MARK_AS_READ_BATCH,
	MARK_AS_UNREAD_BATCH,
	SELECT_ALL_CHECKBOX_TEST_ID,
	SELECT_CHECKBOX_TEST_ID,
	SELECTED_ITEMS_COUNT,
} from '@copilot/common/pages/inboxV2/components/listing/constants';
import LoadingIndicator from '@copilot/common/components/loadingIndicator/spinner';

// TODO: Integrate callbacks for batch actions (COPILOT-7903)
function BatchActions({
	selected,
	selectableProps: {
		onAddToCampaign,
		onApplyTags,
		onDelete: handleDeleteMessages,
		onMarkAsRead: handleMarkAsRead,
		onMarkAsUnread: handleMarkAsUnread,
	},
	onClearSelection,
}: {
	selected: string[];
	selectableProps: SelectionProps;
	onClearSelection: () => void;
}) {
	const [isLoading, setIsLoading] = useState(false);

	async function onMarkAsRead(threadIds: string[]) {
		setIsLoading(true);
		await handleMarkAsRead(threadIds);
		setIsLoading(false);
		onClearSelection();
	}

	async function onMarkAsUnread(threadIds: string[]) {
		setIsLoading(true);
		await handleMarkAsUnread(threadIds);
		setIsLoading(false);
		onClearSelection();
	}

	async function onDeleteMessages(threadIds: string[]) {
		setIsLoading(true);
		await handleDeleteMessages(threadIds);
		setIsLoading(false);
		onClearSelection();
	}

	return (
		<HidableBottomPanel hidden={isEmpty(selected)}>
			<Flex className={styles.batchActionsContainer}>
				<Space wrap>
					{isLoading && <Spin indicator={<LoadingOutlined />} />}
					<Text>{BATCH_ACTION_TITLE}</Text>
					<Button onClick={onApplyTags} disabled={isLoading}>
						{APPLY_TAGS_BATCH}
					</Button>
					<Button onClick={() => void onMarkAsRead(selected)} disabled={isLoading}>
						{MARK_AS_READ_BATCH}
					</Button>
					<Button onClick={() => void onMarkAsUnread(selected)} disabled={isLoading}>
						{MARK_AS_UNREAD_BATCH}
					</Button>
					<Popconfirm
						title={
							<>
								<Text>Deleting this conversation clears it from your Inbox</Text>
								<Text>but retains it under 'Connections'.</Text>
							</>
						}
						placement={'topRight'}
						okText="Delete"
						onConfirm={() => void onDeleteMessages(selected)}
						disabled={isLoading}
					>
						<Button disabled={isLoading}>{DELETE_BATCH}</Button>
					</Popconfirm>
					<Button onClick={onAddToCampaign} disabled={isLoading}>
						<Space>
							{ADD_TO_CAMPAIGN_BATCH}
							<DownOutlined />
						</Space>
					</Button>
				</Space>
				<Text>{`${SELECTED_ITEMS_COUNT} ${selected.length}`}</Text>
			</Flex>
		</HidableBottomPanel>
	);
}

export default function InboxV2List({
	messages,
	render,
	hasFilter,
	isLoading,
	canLoadMore,
	isLoadingMore,
	onLoadMore,
	selectionProps,
	extraHeaderContent,
}: InboxV2ListProps & InboxV2ListRenderProps) {
	const isSelectable = !isNil(selectionProps);
	const allIds = messages.map((item) => item.id);
	const [selectedItems, setSelectedItems] = useState<string[]>([]);

	function onSelected(id: string, checked: boolean) {
		if (checked && !selectedItems.includes(id)) {
			setSelectedItems(selectedItems.concat(id));
		} else {
			setSelectedItems(selectedItems.filter((item) => item !== id));
		}
	}

	function onSelectAll(checked: boolean) {
		if (checked) {
			setSelectedItems(selectedItems.concat(pullAll(allIds, selectedItems)));
		} else {
			setSelectedItems([]);
		}
	}

	return (
		<LoadingIndicator isLoading={isLoading}>
			<div className={styles.headerSection}>
				{isSelectable && (
					<Checkbox
						data-testid={SELECT_ALL_CHECKBOX_TEST_ID}
						className={styles.checkbox}
						indeterminate={
							selectedItems.length > 0 && selectedItems.length < messages.length
						}
						onChange={(e) => onSelectAll(e.target.checked)}
						checked={
							selectedItems.length > 0 && messages.length === selectedItems.length
						}
					/>
				)}
				<InboxV2SearchBar
					extraContent={extraHeaderContent}
					searchPlaceholder={'Search By Name'}
				/>
			</div>
			{isEmpty(messages) ? (
				<InboxPageV2NoMessagePlaceholder hasFilter={hasFilter} />
			) : (
				<>
					<div data-testid={MESSAGE_LIST_TEST_ID} className={styles.messageList}>
						{messages.map((item, idx) => (
							<Flex gap={'small'} className={styles.messageItem} key={item.id}>
								{isSelectable ? (
									<>
										<Checkbox
											data-testid={SELECT_CHECKBOX_TEST_ID}
											className={styles.checkbox}
											checked={selectedItems.includes(item.id)}
											onChange={(e) => onSelected(item.id, e.target.checked)}
										/>
										{render(item, idx, selectedItems.includes(item.id))}
									</>
								) : (
									render(item, idx)
								)}
							</Flex>
						))}
					</div>
					{canLoadMore && (
						<div className={styles.loadMoreContainer}>
							<Button
								block
								data-testid={LOAD_MORE_BUTTON_TEST_ID}
								className={styles.loadMoreButton}
								loading={isLoadingMore}
								disabled={isLoadingMore}
								onClick={() => onLoadMore(messages.length)}
							>
								{LOAD_MORE_BUTTON}
							</Button>
						</div>
					)}
				</>
			)}
			{isSelectable && (
				<BatchActions
					selected={selectedItems}
					selectableProps={selectionProps}
					onClearSelection={() => setSelectedItems([])}
				/>
			)}
		</LoadingIndicator>
	);
}
